// SEO Configuration and utilities
export const seoConfig = {
  siteName: "Akiblackcar",
  siteUrl: "https://car.akiblackcar.com",
  description: "Premier luxury limousine and black car service in Washington DC, Maryland, and Virginia. Professional chauffeurs, luxury vehicles, and exceptional service for all occasions.",
  keywords: [
    "limo service DC",
    "black car service Washington DC",
    "luxury transportation Maryland",
    "limousine rental Virginia",
    "airport transfer DC",
    "wedding limo service",
    "corporate transportation",
    "chauffeur service",
    "luxury car service",
    "DMV limo service"
  ],
  social: {
    twitter: "@akiblackcar",
    facebook: "akiblackcar",
    instagram: "akiblackcar"
  },
  business: {
    name: "Akiblackcar",
    type: "TransportationService",
    address: {
      streetAddress: "Washington DC Area",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20001",
      addressCountry: "US"
    },
    phone: "+1-202-555-0123",
    email: "info@akiblackcar.com",
    website: "https://car.akiblackcar.com",
    description: "Premier luxury limousine and black car service in the DMV area",
    priceRange: "$$$",
    serviceArea: ["Washington DC", "Maryland", "Virginia"],
    hours: "24/7",
    foundingDate: "2014"
  }
};

// Generate structured data for business
export const generateBusinessStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "TransportationService",
    "name": seoConfig.business.name,
    "description": seoConfig.business.description,
    "url": seoConfig.business.website,
    "telephone": seoConfig.business.phone,
    "email": seoConfig.business.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": seoConfig.business.address.streetAddress,
      "addressLocality": seoConfig.business.address.addressLocality,
      "addressRegion": seoConfig.business.address.addressRegion,
      "postalCode": seoConfig.business.address.postalCode,
      "addressCountry": seoConfig.business.address.addressCountry
    },
    "areaServed": seoConfig.business.serviceArea.map(area => ({
      "@type": "City",
      "name": area
    })),
    "priceRange": seoConfig.business.priceRange,
    "openingHours": "Mo-Su 00:00-23:59",
    "foundingDate": seoConfig.business.foundingDate,
    "serviceType": [
      "Airport Transfer",
      "Wedding Transportation",
      "Corporate Transportation",
      "City Tours",
      "Special Events",
      "Hourly Service"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Luxury Transportation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Airport Transfer",
            "description": "Professional airport transportation service"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Wedding Transportation",
            "description": "Elegant wedding limousine service"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Corporate Transportation",
            "description": "Business transportation solutions"
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    }
  };
};

// Generate page-specific meta tags
export const generatePageMeta = (pageData) => {
  const {
    title,
    description,
    keywords = [],
    image,
    url,
    type = "website",
    noindex = false
  } = pageData;

  const fullTitle = title ? `${title} | ${seoConfig.siteName}` : seoConfig.siteName;
  const fullDescription = description || seoConfig.description;
  const fullKeywords = [...seoConfig.keywords, ...keywords].join(", ");
  const fullUrl = url ? `${seoConfig.siteUrl}${url}` : seoConfig.siteUrl;
  const fullImage = image ? `${seoConfig.siteUrl}${image}` : `${seoConfig.siteUrl}/og-image.jpg`;

  return {
    title: fullTitle,
    description: fullDescription,
    keywords: fullKeywords,
    url: fullUrl,
    image: fullImage,
    type,
    noindex,
    openGraph: {
      title: fullTitle,
      description: fullDescription,
      url: fullUrl,
      image: fullImage,
      type,
      siteName: seoConfig.siteName
    },
    twitter: {
      card: "summary_large_image",
      title: fullTitle,
      description: fullDescription,
      image: fullImage,
      creator: seoConfig.social.twitter
    }
  };
};

// Page-specific SEO data
export const pageSeoData = {
  home: {
    title: "Premier Limo Service in DC, Maryland, Virginia | Akiblackcar",
    description: "Experience luxury transportation with Akiblackcar. Professional limo and black car service in Washington DC, Maryland, and Virginia. Book your ride today!",
    keywords: ["limo service DC", "luxury transportation", "black car service", "chauffeur service"],
    url: "/"
  },
  booking: {
    title: "Book Your Luxury Ride | Akiblackcar",
    description: "Book your luxury limousine or black car service online. Professional chauffeurs, luxury vehicles, and exceptional service for all occasions.",
    keywords: ["book limo", "limo booking", "luxury car booking", "transportation booking"],
    url: "/booking"
  },
  services: {
    title: "Our Services | Luxury Transportation | Akiblackcar",
    description: "Discover our comprehensive luxury transportation services including airport transfers, wedding transportation, corporate events, and city tours.",
    keywords: ["limo services", "transportation services", "wedding limo", "corporate transportation"],
    url: "/services"
  },
  fleet: {
    title: "Our Luxury Fleet | Akiblackcar",
    description: "Explore our premium fleet of luxury vehicles including sedans, SUVs, stretch limousines, and party buses for every occasion.",
    keywords: ["luxury fleet", "limo fleet", "luxury vehicles", "premium cars"],
    url: "/fleet"
  },
  contact: {
    title: "Contact Us | Akiblackcar",
    description: "Get in touch with Akiblackcar for your luxury transportation needs. Professional service, competitive rates, and 24/7 availability.",
    keywords: ["contact limo service", "transportation contact", "limo booking contact"],
    url: "/contact"
  },
  reviews: {
    title: "Customer Reviews | Akiblackcar",
    description: "Read what our satisfied customers say about their experience with Akiblackcar's luxury transportation services.",
    keywords: ["limo reviews", "customer testimonials", "transportation reviews"],
    url: "/reviews"
  }
};

// Generate sitemap data
export const generateSitemapData = () => {
  const pages = Object.values(pageSeoData);
  return pages.map(page => ({
    url: `${seoConfig.siteUrl}${page.url}`,
    lastmod: new Date().toISOString().split('T')[0],
    changefreq: 'weekly',
    priority: page.url === '/' ? '1.0' : '0.8'
  }));
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `User-agent: *
Allow: /

Sitemap: ${seoConfig.siteUrl}/sitemap.xml

# Disallow admin areas
Disallow: /admin/
Disallow: /api/

# Allow search engines to crawl all public pages
Allow: /booking
Allow: /services
Allow: /fleet
Allow: /contact
Allow: /reviews`;
};
